import { Howl } from 'howler';

// Audio context for synthesized sound effects (victory, new round only)
let audioCtx = null;

const getAudioContext = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

// Master volume (0 to 1)
let masterVolume = 1; // Aumentato per gli effetti sonori
let musicVolume = 0.2;  // Musica di sottofondo (lobby)
let battleMusicVolume = 0.02; // Musica di gioco, abbassata
let isMuted = false;

// Detect if the device is mobile
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// Adjust volume for mobile
if (isMobile) {
  musicVolume = 0.1;  // Lower music volume for mobile
  battleMusicVolume = 0.01;  // Lower battle music for mobile
}

// Background music Howl instances
let lobbyMusic = null;
let battleMusic = null;
let currentMusic = null; // 'lobby' | 'battle' | null

export const setVolume = (vol) => {
  masterVolume = Math.max(0, Math.min(1, vol));
};

export const setMusicVolume = (vol) => {
  musicVolume = Math.max(0, Math.min(1, vol));
  if (lobbyMusic) lobbyMusic.volume(isMuted ? 0 : musicVolume);
  if (battleMusic) battleMusic.volume(isMuted ? 0 : battleMusicVolume);
};

export const toggleMute = () => {
  isMuted = !isMuted;

  // Update music volume based on mute state
  if (lobbyMusic) lobbyMusic.volume(isMuted ? 0 : musicVolume);
  if (battleMusic) battleMusic.volume(isMuted ? 0 : battleMusicVolume);

  // Mute/unmute card SFX and jolly
  Object.values(cardSfxCache).forEach(h => h.volume(isMuted ? 0 : masterVolume));
  if (jollySfx) jollySfx.volume(isMuted ? 0 : masterVolume);

  // Also mute/unmute globally with Howler
  Howler.mute(isMuted);

  return isMuted;
};

export const getMuteState = () => isMuted;

// --- Card SFX (MP3 files via Howler.js) ---
// Each card value (1-10) has its own dedicated sound file.

const cardSfxCache = {};
let jollySfx = null;

const getCardSfx = (cardValue) => {
  if (!cardSfxCache[cardValue]) {
    cardSfxCache[cardValue] = new Howl({
      src: [`/audio/sfx/card_${cardValue}.mp3`],
      volume: isMuted ? 0 : masterVolume,
    });
  }
  return cardSfxCache[cardValue];
};

const getJollySfx = () => {
  if (!jollySfx) {
    jollySfx = new Howl({
      src: ['/audio/sfx/jolly.mp3'],
      volume: isMuted ? 0 : masterVolume,
    });
  }
  return jollySfx;
};

// --- Public SFX API ---

export const playCardSound = (cardValue) => {
  try {
    if (cardValue >= 1 && cardValue <= 10) {
      const sfx = getCardSfx(cardValue);
      sfx.volume(isMuted ? 0 : masterVolume);
      sfx.play();
    }
  } catch (e) {
    console.warn('[SOUND] Failed to play card sound:', e);
  }
};

export const playJollySound = () => {
  try {
    const sfx = getJollySfx();
    sfx.volume(isMuted ? 0 : masterVolume);
    sfx.play();
  } catch (e) {
    console.warn('[SOUND] Failed to play jolly sound:', e);
  }
};

// --- Synthesized victory/round sounds (AudioContext) ---

const playVictorySound = () => {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  const melody = [
    { freq: 523, time: 0 }, { freq: 659, time: 0.12 }, { freq: 784, time: 0.24 },
    { freq: 1047, time: 0.36 }, { freq: 784, time: 0.52 }, { freq: 1047, time: 0.64 },
    { freq: 1319, time: 0.80 },
  ];
  melody.forEach(({ freq, time }) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now + time);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(masterVolume * 0.3, now + time);
    gain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.4);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now + time);
    osc.stop(now + time + 0.5);
  });
};

const playNewRoundSound = () => {
  const ctx = getAudioContext();
  const now = ctx.currentTime;
  for (let i = 0; i < 5; i++) {
    const bufferSize = ctx.sampleRate * 0.04;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let j = 0; j < bufferSize; j++) {
      data[j] = (Math.random() * 2 - 1) * Math.exp(-j / (ctx.sampleRate * 0.01));
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2000 + i * 500;
    filter.Q.value = 0.5;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(masterVolume * 0.15, now + i * 0.06);
    gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.06 + 0.05);
    source.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    source.start(now + i * 0.06);
  }
};

export const playVictory = () => {
  try { playVictorySound(); } catch (e) { console.warn('[SOUND] Victory failed:', e); }
};

export const playNewRound = () => {
  try { playNewRoundSound(); } catch (e) { console.warn('[SOUND] New round failed:', e); }
};

export const initAudio = () => {
  try { getAudioContext(); } catch (e) { console.warn('[SOUND] Init failed:', e); }
};

// --- Background Music (MP3 files via Howler.js) ---
// Versione fissa invece di Date.now(): con un parametro sempre diverso il
// browser riscarica 5 MB di musica a ogni partita. Va alzata a mano quando
// si sostituisce una traccia.
const MUSIC_VERSION = '2';

const ensureLobbyHowl = () => {
  if (!lobbyMusic) {
    const v = MUSIC_VERSION;
    lobbyMusic = new Howl({
      src: [`/audio/lobby.mp3?v=${v}`],
      loop: true,
      volume: isMuted ? 0 : musicVolume,
      html5: true,
    });
  }
  return lobbyMusic;
};

const ensureBattleHowl = () => {
  if (!battleMusic) {
    const v = MUSIC_VERSION;
    battleMusic = new Howl({
      src: [`/audio/game.mp3?v=${v}`],
      loop: true,
      volume: isMuted ? 0 : battleMusicVolume,
      html5: true,
    });
  }
  return battleMusic;
};

export const startLobbyMusic = () => {
  try {
    stopAllMusic();
    const howl = ensureLobbyHowl();
    howl.volume(isMuted ? 0 : musicVolume);
    howl.play();
    currentMusic = 'lobby';
    console.log('[MUSIC] Lobby music started (MP3)');
  } catch (e) {
    console.warn('[MUSIC] Failed to start lobby music:', e);
  }
};

export const startBattleMusic = () => {
  try {
    stopAllMusic();
    const howl = ensureBattleHowl();
    howl.volume(isMuted ? 0 : battleMusicVolume);
    howl.play();
    currentMusic = 'battle';
    console.log('[MUSIC] Battle music started (MP3, low volume)');
  } catch (e) {
    console.warn('[MUSIC] Failed to start battle music:', e);
  }
};

export const stopAllMusic = () => {
  if (lobbyMusic) { lobbyMusic.stop(); lobbyMusic.unload(); lobbyMusic = null; }
  if (battleMusic) { battleMusic.stop(); battleMusic.unload(); battleMusic = null; }
  currentMusic = null;
};

export const getCurrentMusic = () => currentMusic;
